import { useRollbar } from '@rollbar/react'
import useEmblaCarousel from 'embla-carousel-react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import CloseIcon from 'shared/icons/close-icon'
import DownloadIcon from 'shared/icons/download-icon'
import ThinArrowRightIcon from 'shared/icons/thin-arrow-right-icon'
import { getAttachmentFilename } from 'shared/utils/file/get-filename'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { galleryFactory } from 'modules/community/components/attachment-gallery/gallery-factory'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'

export interface AttachmentGalleryProps {}

export const AttachmentGallery = () => {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const { attachments, initialGallerySlide, setIsGalleryVisible } = usePostAttachmentsContext()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: 'x',
    startIndex: initialGallerySlide || 0,
    dragFree: false,
    containScroll: 'trimSnaps',
  })

  const [selectedIndex, setSelectedIndex] = useState(initialGallerySlide || 0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const activeAttachment = attachments?.[selectedIndex]
  const canDownload =
    activeAttachment?.type === AttachmentTypeEnum.Image ||
    activeAttachment?.type === AttachmentTypeEnum.EmbeddedGif

  const updateSelection = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateSelection()
    emblaApi.on('select', updateSelection)
    emblaApi.on('reInit', updateSelection)

    return () => {
      emblaApi.off('select', updateSelection)
      emblaApi.off('reInit', updateSelection)
    }
  }, [emblaApi, updateSelection])

  // native download only works for same-origin uris
  const handleDownload = async () => {
    if (!activeAttachment?.url) {
      toast.error(t('core.error.internal_error_message'))
      return
    }

    try {
      const response = await fetch(activeAttachment.url)
      if (!response.ok) {
        rollbar.error('Error downloading attachment', {
          url: activeAttachment.url,
          status: response.status,
        })
        return
      }

      const blob = await response.blob()
      const contentDisposition = response.headers.get('content-disposition')
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = getAttachmentFilename(activeAttachment.url, {
        contentDisposition,
        contentType: blob.type,
      })

      document.body.appendChild(link)
      link.click()
      link.remove()

      setTimeout(() => URL.revokeObjectURL(url), 300)
    } catch (error) {
      rollbar.error('Error downloading attachment', {
        url: activeAttachment.url,
        error: error instanceof Error ? error.message : 'Unknown error',
        errorObject: error,
      })
    }
  }

  // keyboard navigation support
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      e.stopPropagation()

      switch (e.key) {
        case 'ArrowLeft':
          scrollPrev()
          return
        case 'ArrowRight':
          scrollNext()
          return
        case 'Escape':
          setIsGalleryVisible(false)
          return
        default:
          return
      }
    }

    window.addEventListener('keydown', handle, true)

    return () => {
      window.removeEventListener('keydown', handle, true)
    }
  }, [scrollNext, scrollPrev, setIsGalleryVisible])

  return createPortal(
    <div className="fixed inset-0 z-[1001] bg-black">
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {attachments?.map(attachment => (
            <div key={attachment.id} className="min-w-0 flex-[0_0_100%]">
              {galleryFactory(attachment)}
            </div>
          ))}
        </div>
      </div>

      <div>
        {canScrollPrev && (
          <button
            aria-label={t('global.previous')}
            className="absolute left-3 top-1/2 -translate-y-1/2 lg:left-12"
            onClick={scrollPrev}
          >
            <ThinArrowRightIcon className="rotate-180 stroke-background-secondary" />
          </button>
        )}
        {canScrollNext && (
          <button
            aria-label={t('global.next')}
            className="absolute right-3 top-1/2 -translate-y-1/2 lg:right-12"
            onClick={scrollNext}
          >
            <ThinArrowRightIcon className="stroke-background-secondary" />
          </button>
        )}
      </div>

      <div className="absolute right-3 top-3 inline-flex items-center gap-8 lg:right-12 lg:top-12">
        {canDownload && (
          <button type="button" onClick={handleDownload}>
            <DownloadIcon
              className="cursor-pointer fill-background-secondary"
              height={21}
              width={21}
            />
          </button>
        )}
        <button type="button" onClick={() => setIsGalleryVisible(false)}>
          <CloseIcon className="cursor-pointer fill-background-secondary" height={25} width={25} />
        </button>
      </div>
    </div>,
    document.body,
  )
}
